import { z } from 'zod';
import { AcademicYear, FamilyId } from '../types/branded';
import { CommunicationSchema } from './communication';
import { FamilyAccountTransactionSchema } from './family-account-transaction';
import { FamilyBalanceSummarySchema } from './family-balance-summary';
import { FamilyInteractionSchema } from './family-interaction';
import { FamilyPaymentMethodSchema } from './family-payment-method';
import { FamilySchema } from './family';
import { ParentContactSchema } from './parent-contact';
import { StudentDocumentSchema } from './student-document';
import { StudentSchema } from './student';

const buildConnectionSchema = <TItem extends z.ZodTypeAny>(itemSchema: TItem) => (
  z.object({
    items: z.array(itemSchema).default([]),
    totalCount: z.number().int().nonnegative(),
    nextToken: z.string().nullable().optional(),
  })
);

export const StudentsDashboardFamilyPreviewSchema = z.object({
  family: FamilySchema,
  students: z.array(StudentSchema).default([]),
  studentCount: z.number().int().nonnegative(),
});
export type StudentsDashboardFamilyPreview = z.infer<typeof StudentsDashboardFamilyPreviewSchema>;

export const StudentsDashboardTreeSummarySchema = z.object({
  totalFamilies: z.number().int().nonnegative(),
  totalStudents: z.number().int().nonnegative(),
});
export type StudentsDashboardTreeSummary = z.infer<typeof StudentsDashboardTreeSummarySchema>;

export const StudentsDashboardTreeSchema = z.object({
  families: z.array(StudentsDashboardFamilyPreviewSchema).default([]),
  totalCount: z.number().int().nonnegative(),
  summary: StudentsDashboardTreeSummarySchema,
});
export type StudentsDashboardTree = z.infer<typeof StudentsDashboardTreeSchema>;

export const FamilyDetailCoreTreeSchema = z.object({
  familyId: FamilyId,
  family: FamilySchema,
  students: z.array(StudentSchema).default([]),
  parentContacts: z.array(ParentContactSchema).default([]),
  communications: z.array(CommunicationSchema).default([]),
});
export type FamilyDetailCoreTree = z.infer<typeof FamilyDetailCoreTreeSchema>;

export const FamilyDetailTuitionTreeSchema = z.object({
  familyId: FamilyId,
  academicYear: AcademicYear,
  balance: FamilyBalanceSummarySchema,
  paymentMethods: buildConnectionSchema(FamilyPaymentMethodSchema),
  transactions: buildConnectionSchema(FamilyAccountTransactionSchema),
});
export type FamilyDetailTuitionTree = z.infer<typeof FamilyDetailTuitionTreeSchema>;

export const FamilyDetailDocumentsTreeSchema = z.object({
  familyId: FamilyId,
  documents: buildConnectionSchema(StudentDocumentSchema),
});
export type FamilyDetailDocumentsTree = z.infer<typeof FamilyDetailDocumentsTreeSchema>;

export const FamilyDetailNotesTreeSchema = z.object({
  familyId: FamilyId,
  timeline: buildConnectionSchema(FamilyInteractionSchema),
});
export type FamilyDetailNotesTree = z.infer<typeof FamilyDetailNotesTreeSchema>;
